import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Plus, LogOut, Search, ArrowLeft, Menu, X, Calendar, Phone, Mail, 
  Building2, MapPin, Edit, Trash2, Download, FileText, ChevronRight, MessageCircle, Upload,
  History, Clock, Euro, Lightbulb
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMDashboard = ({ user, onLogout }) => {
  const [customers, setCustomers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterProgram, setFilterProgram] = useState('all');
  const [filterEmeMin, setFilterEmeMin] = useState('');
  const [filterEmeMax, setFilterEmeMax] = useState('');
  const [filterStartDateFrom, setFilterStartDateFrom] = useState('');
  const [filterKad, setFilterKad] = useState('');
  const [filterNoSale, setFilterNoSale] = useState(false);
  const [customersWithOpenSale, setCustomersWithOpenSale] = useState(new Set());
  const [notInterestedMap, setNotInterestedMap] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Region autocomplete state
  const [regionSearch, setRegionSearch] = useState('');
  const [showRegionSuggestions, setShowRegionSuggestions] = useState(false);
  const regionInputRef = useRef(null);
  
  // Communication dialog (inline from search results)
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commCustomer, setCommCustomer] = useState(null);
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [historyCommunications, setHistoryCommunications] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [commForm, setCommForm] = useState({
    communication_type: 'call',
    outcome: '',
    notes: '',
    next_contact_date: '',
    next_contact_time: '10:00',
    estimated_budget: '',
    business_idea: '',
    assigned_to: '',
    program_id: '',
    interest_status: ''
  });
  
  // KAD file upload
  const [kadFile, setKadFile] = useState(null);
  const [uploadingKad, setUploadingKad] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_type: 'existing',
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    region_id: '',
    tax_id: '',
    notes: '',
    // Existing business fields
    business_category: '',
    start_date: '',
    eme: '',
    annual_revenue: '',
    kad: '',
    // New (υπό σύσταση) business fields
    planned_start_date: '',
    business_idea: '',
    estimated_budget: '',
    program_interests: []
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
    fetchRegions();
    fetchPrograms();
    fetchCategories();
    if (user.role === 'owner') {
      fetchTeamMembers();
    }
  }, []);

  // Close region suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionInputRef.current && !regionInputRef.current.contains(event.target)) {
        setShowRegionSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const [custRes, apptRes, notIntRes] = await Promise.all([
        axios.get(`${API}/crm/customers?user_id=${user.user_id}`),
        axios.get(`${API}/crm/appointments?user_id=${user.user_id}&status=pending`),
        axios.get(`${API}/crm/communications/not-interested?user_id=${user.user_id}`)
      ]);
      setCustomers(custRes.data);
      // Build set of customer_ids that have open appointments
      const openSet = new Set();
      (apptRes.data || []).forEach(a => { if (a.customer_id) openSet.add(a.customer_id); });
      setCustomersWithOpenSale(openSet);
      // Build map: program_id -> Set of customer_ids that said "not interested"
      const niMap = {};
      (notIntRes.data || []).forEach(c => {
        const pid = c.program_interest;
        if (pid) {
          if (!niMap[pid]) niMap[pid] = new Set();
          niMap[pid].add(c.customer_id);
        }
      });
      setNotInterestedMap(niMap);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης πελατών');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/crm/regions?user_id=${user.user_id}`);
      setRegions(response.data);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/crm/programs?user_id=${user.user_id}`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/crm/categories?user_id=${user.user_id}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members?user_id=${user.user_id}`);
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // Filter regions based on search input
  const filteredRegions = useMemo(() => {
    if (!regionSearch) return regions;
    return regions.filter(r => 
      r.name.toLowerCase().includes(regionSearch.toLowerCase())
    );
  }, [regions, regionSearch]);

  // Validate ΑΦΜ (9 digits only)
  const validateTaxId = (value) => {
    return value.replace(/\D/g, '').slice(0, 9);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name.trim()) {
      toast.error('Η επωνυμία είναι υποχρεωτική');
      return;
    }

    if (formData.tax_id && formData.tax_id.length !== 9) {
      toast.error('Το ΑΦΜ πρέπει να είναι 9 ψηφία');
      return;
    }

    // Duplicate check (only for NEW customers, not editing)
    if (!editingCustomer) {
      try {
        const params = new URLSearchParams({ user_id: user.user_id });
        if (formData.company_name) params.append('company_name', formData.company_name.trim());
        if (formData.tax_id) params.append('tax_id', formData.tax_id.trim());
        if (formData.phone) params.append('phone', formData.phone.trim());
        
        const dupRes = await axios.get(`${API}/crm/customers/check-duplicate?${params}`);
        if (dupRes.data.duplicates?.length > 0) {
          const names = dupRes.data.duplicates.map(d => d.company_name).join(', ');
          const confirmed = window.confirm(
            `Υπάρχει ήδη πελάτης με παρόμοια στοιχεία:\n${names}\n\nΘέλετε να συνεχίσετε;`
          );
          if (!confirmed) return;
        }
      } catch (err) {
        // If check fails, continue with creation
      }
    }

    try {
      setLoading(true);
      
      const submitData = {
        ...formData,
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        mobile: formData.mobile?.trim() || null,
        contact_person: formData.contact_person?.trim() || null,
        address: formData.address?.trim() || null,
        tax_id: formData.tax_id?.trim() || null,
        kad: formData.kad?.trim() || null,
        notes: formData.notes?.trim() || null,
        eme: formData.eme ? parseFloat(formData.eme) : null,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        estimated_budget: formData.estimated_budget ? parseFloat(formData.estimated_budget) : null,
        region_id: formData.region_id || null
      };

      let customerId;
      if (editingCustomer) {
        await axios.put(
          `${API}/crm/customers/${editingCustomer.customer_id}?user_id=${user.user_id}`,
          submitData
        );
        customerId = editingCustomer.customer_id;
        toast.success('Ο πελάτης ενημερώθηκε');
      } else {
        const response = await axios.post(`${API}/crm/customers?user_id=${user.user_id}`, submitData);
        customerId = response.data.customer_id;
        toast.success('Ο πελάτης δημιουργήθηκε');
      }
      
      // Upload KAD file if selected
      if (kadFile && customerId) {
        await uploadKadFile(customerId);
      }
      
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Σφάλμα αποθήκευσης';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMessage = errorDetail.map(e => e.msg || e.message || 'Σφάλμα').join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const uploadKadFile = async (customerId) => {
    try {
      setUploadingKad(true);
      const formData = new FormData();
      formData.append('file', kadFile);
      formData.append('description', 'ΚΑΔ Δραστηριότητας');
      
      await axios.post(
        `${API}/crm/customers/${customerId}/files?user_id=${user.user_id}&description=ΚΑΔ Δραστηριότητας`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Το αρχείο ΚΑΔ ανέβηκε');
    } catch (error) {
      toast.error('Σφάλμα ανεβάσματος αρχείου ΚΑΔ');
    } finally {
      setUploadingKad(false);
      setKadFile(null);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον πελάτη;')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/crm/customers/${customerId}?user_id=${user.user_id}`);
      toast.success('Ο πελάτης διαγράφηκε');
      fetchCustomers();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_type: customer.customer_type || 'existing',
      company_name: customer.company_name || '',
      contact_person: customer.contact_person || '',
      email: customer.email || '',
      phone: customer.phone || '',
      mobile: customer.mobile || '',
      address: customer.address || '',
      region_id: customer.region_id || '',
      tax_id: customer.tax_id || '',
      notes: customer.notes || '',
      business_category: customer.business_category || '',
      start_date: customer.start_date || '',
      eme: customer.eme || '',
      annual_revenue: customer.annual_revenue || '',
      kad: customer.kad || '',
      planned_start_date: customer.planned_start_date || '',
      business_idea: customer.business_idea || '',
      estimated_budget: customer.estimated_budget || '',
      program_interests: customer.program_interests || []
    });
    setRegionSearch(customer.region_name || '');
    setKadFile(null);
    setDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/crm/export/excel?user_id=${user.user_id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CRM_Pelates_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Εξαγωγή ολοκληρώθηκε');
    } catch (error) {
      toast.error('Σφάλμα εξαγωγής');
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setRegionSearch('');
    setKadFile(null);
    setFormData({
      customer_type: 'existing',
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      region_id: '',
      tax_id: '',
      notes: '',
      business_category: '',
      start_date: '',
      eme: '',
      annual_revenue: '',
      kad: '',
      planned_start_date: '',
      business_idea: '',
      estimated_budget: '',
      program_interests: []
    });
  };

  const openNewCustomerDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const selectRegion = (region) => {
    setFormData(prev => ({ ...prev, region_id: region.region_id }));
    setRegionSearch(region.name);
    setShowRegionSuggestions(false);
  };

  // Communication from search results
  const openCommunicationDialog = (customer, e) => {
    e.stopPropagation();
    setCommCustomer(customer);
    setCommForm({
      communication_type: 'call',
      outcome: '',
      notes: '',
      next_contact_date: '',
      next_contact_time: '10:00',
      estimated_budget: '',
      business_idea: '',
      assigned_to: '',
      program_id: '',
      interest_status: ''
    });
    setCommDialogOpen(true);
  };

  const openHistoryDialog = async (customer, e) => {
    e.stopPropagation();
    setHistoryCustomer(customer);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    setHistoryCommunications([]);
    try {
      const response = await axios.get(
        `${API}/crm/customers/${customer.customer_id}/communications?user_id=${user.user_id}`
      );
      setHistoryCommunications(response.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Σφάλμα φόρτωσης ιστορικού');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('el-GR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const handleCreateCommunication = async (e) => {
    e.preventDefault();
    
    if (!commForm.outcome.trim()) {
      toast.error('Το αποτέλεσμα είναι υποχρεωτικό');
      return;
    }

    // Date required ONLY if interested (creates appointment)
    const isNotInterested = commForm.interest_status === 'not_interested';
    if (!isNotInterested && !commForm.next_contact_date) {
      toast.error('Η ημερομηνία ραντεβού είναι υποχρεωτική');
      return;
    }

    // Check for open appointments when scheduling a new one
    if (commForm.next_contact_date && commCustomer) {
      try {
        const openRes = await axios.get(
          `${API}/crm/customers/${commCustomer.customer_id}/open-appointments?user_id=${user.user_id}`
        );
        if (openRes.data.count > 0) {
          const confirmed = window.confirm(
            `Υπάρχει ήδη ανοιχτή πώληση σε εξέλιξη για τον πελάτη "${commCustomer.company_name}" (${openRes.data.count} ραντεβού).\n\nΕίστε σίγουροι ότι επιθυμείτε να συνεχίσετε σε νέα επικοινωνία;`
          );
          if (!confirmed) return;
        }
      } catch (err) {
        // If check fails, continue
      }
    }
    
    try {
      const data = {
        customer_id: commCustomer.customer_id,
        communication_type: commForm.communication_type,
        outcome: commForm.outcome,
        notes: commForm.notes,
        // If not interested, don't create appointment (clear date)
        next_contact_date: isNotInterested ? null : (commForm.next_contact_date || null),
        estimated_budget: commForm.estimated_budget ? parseFloat(commForm.estimated_budget) : null,
        business_idea: commForm.business_idea || null,
        program_interest: commForm.program_id || null,
        interest_status: commForm.interest_status || null
      };

      // If owner assigns to someone else, handle "self" value
      const assignedUserId = (commForm.assigned_to === 'self' || !commForm.assigned_to) 
        ? user.user_id 
        : commForm.assigned_to;
      
      await axios.post(`${API}/crm/communications?user_id=${assignedUserId}`, data);
      
      toast.success(isNotInterested 
        ? 'Η επικοινωνία καταχωρήθηκε - Δεν ενδιαφέρεται' 
        : 'Η επικοινωνία καταχωρήθηκε και δημιουργήθηκε ραντεβού');
      setCommDialogOpen(false);
      setCommCustomer(null);
      
      // Refresh all data (customers + appointments + not-interested)
      fetchCustomers();
    } catch (error) {
      toast.error('Σφάλμα καταχώρησης');
    }
  };

  // Filter customers with advanced filters
  const filteredCustomers = customers.filter(customer => {
    // Text search (includes ΑΦΜ)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        customer.company_name?.toLowerCase().includes(query) ||
        customer.contact_person?.toLowerCase().includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.phone?.includes(query) ||
        customer.mobile?.includes(query) ||
        customer.tax_id?.includes(query);
      if (!matchesSearch) return false;
    }
    
    // Type filter
    if (filterType !== 'all' && customer.customer_type !== filterType) {
      return false;
    }
    
    // Region filter
    if (filterRegion !== 'all' && customer.region_id !== filterRegion) {
      return false;
    }
    
    // Program filter
    if (filterProgram !== 'all' && !customer.program_interests?.includes(filterProgram)) {
      return false;
    }
    
    // EME filter
    if (filterEmeMin && customer.eme !== null && customer.eme < parseFloat(filterEmeMin)) {
      return false;
    }
    if (filterEmeMax && customer.eme !== null && customer.eme > parseFloat(filterEmeMax)) {
      return false;
    }
    
    // Start date filter
    if (filterStartDateFrom && customer.start_date) {
      if (customer.start_date < filterStartDateFrom) {
        return false;
      }
    }
    
    // KAD filter
    if (filterKad) {
      const kadQuery = filterKad.toLowerCase();
      if (!customer.kad?.toLowerCase().includes(kadQuery)) {
        return false;
      }
    }
    
    // No open sale filter
    if (filterNoSale) {
      // Exclude customers with open appointments
      if (customersWithOpenSale.has(customer.customer_id)) {
        return false;
      }
      // If a specific program is selected, also exclude "not interested" for that program
      if (filterProgram !== 'all') {
        const niSet = notInterestedMap[filterProgram];
        if (niSet && niSet.has(customer.customer_id)) {
          return false;
        }
      }
    }
    
    return true;
  });

  const toggleProgramInterest = (programId) => {
    setFormData(prev => {
      const interests = prev.program_interests || [];
      if (interests.includes(programId)) {
        return { ...prev, program_interests: interests.filter(id => id !== programId) };
      } else {
        return { ...prev, program_interests: [...interests, programId] };
      }
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setFilterRegion('all');
    setFilterProgram('all');
    setFilterEmeMin('');
    setFilterEmeMax('');
    setFilterStartDateFrom('');
    setFilterKad('');
    setFilterNoSale(false);
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-md border border-slate-200"
        data-testid="mobile-menu-button"
      >
        <Menu className="h-6 w-6 text-slate-700" />
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay md:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">CRM</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => navigate('/select-module')}
              data-testid="back-to-modules-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Πίσω στην Επιλογή
            </Button>
            
            <div className="pt-4 border-t border-slate-200 mt-4">
              <button
                onClick={() => {}}
                className="w-full px-4 py-2 rounded-md text-left font-medium transition-colors flex items-center bg-slate-100 text-slate-900"
                data-testid="nav-search-customers"
              >
                <Search className="mr-2 h-4 w-4" />
                Αναζήτηση Πελατών
              </button>
              
              <button
                onClick={openNewCustomerDialog}
                className="w-full px-4 py-2 rounded-md text-left font-medium transition-colors flex items-center text-slate-600 hover:bg-slate-50"
                data-testid="nav-add-customer"
              >
                <Plus className="mr-2 h-4 w-4" />
                Προσθήκη Πελάτη
              </button>
              
              <button
                onClick={() => navigate('/crm/appointments')}
                className="w-full px-4 py-2 rounded-md text-left font-medium transition-colors flex items-center text-slate-600 hover:bg-slate-50"
                data-testid="nav-appointments"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Πωλήσεις - Ραντεβού
              </button>
              
              <button
                onClick={() => navigate('/crm/tasks')}
                className="w-full px-4 py-2 rounded-md text-left font-medium transition-colors flex items-center text-slate-600 hover:bg-slate-50"
                data-testid="nav-tasks"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Καθήκοντα & Επικοινωνίες
              </button>
              
              {user.role === 'owner' && (
                <>
                  <div className="pt-4 mt-4 border-t border-slate-200">
                    <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase">Διαχείριση</p>
                  </div>
                  <button
                    onClick={() => navigate('/crm/settings')}
                    className="w-full px-4 py-2 rounded-md text-left font-medium transition-colors flex items-center text-slate-600 hover:bg-slate-50"
                    data-testid="nav-crm-settings"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Ρυθμίσεις CRM
                  </button>
                  <button
                    onClick={() => navigate('/crm/bulk-import')}
                    className="w-full px-4 py-2 rounded-md text-left font-medium transition-colors flex items-center text-slate-600 hover:bg-slate-50"
                    data-testid="nav-bulk-import"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Μαζική Εισαγωγή
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="p-4 md:p-8 pt-16 md:pt-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="crm-heading">
                Πελατολόγιο CRM
              </h1>
              <p className="text-slate-600 mt-1">
                {filteredCustomers.length} πελάτες {customers.length !== filteredCustomers.length && `(από ${customers.length})`}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleExport}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
                data-testid="export-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Excel
              </Button>
              
              <Button
                onClick={openNewCustomerDialog}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="add-customer-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Νέος Πελάτης
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Αναζήτηση (επωνυμία, ΑΦΜ, τηλ)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="Τύπος πελάτη" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλοι οι τύποι</SelectItem>
                  <SelectItem value="existing">Υφιστάμενη Επιχείρηση</SelectItem>
                  <SelectItem value="new">Υπό Σύσταση Επιχείρηση</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterRegion} onValueChange={setFilterRegion}>
                <SelectTrigger data-testid="filter-region">
                  <SelectValue placeholder="Περιφέρεια" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι περιφέρειες</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region.region_id} value={region.region_id}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterProgram} onValueChange={setFilterProgram}>
                <SelectTrigger data-testid="filter-program">
                  <SelectValue placeholder="Πρόγραμμα" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλα τα προγράμματα</SelectItem>
                  {programs.map(program => (
                    <SelectItem key={program.program_id} value={program.program_id}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Advanced filters */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 pt-4 border-t">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">ΕΜΕ από</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Ελάχ."
                  value={filterEmeMin}
                  onChange={(e) => setFilterEmeMin(e.target.value)}
                  data-testid="filter-eme-min"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">ΕΜΕ έως</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Μέγ."
                  value={filterEmeMax}
                  onChange={(e) => setFilterEmeMax(e.target.value)}
                  data-testid="filter-eme-max"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Ημ. Έναρξης από</Label>
                <Input
                  type="date"
                  value={filterStartDateFrom}
                  onChange={(e) => setFilterStartDateFrom(e.target.value)}
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">ΚΑΔ</Label>
                <Input
                  placeholder="π.χ. 62.01"
                  value={filterKad}
                  onChange={(e) => setFilterKad(e.target.value)}
                  data-testid="filter-kad"
                />
              </div>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors w-full" data-testid="filter-no-sale">
                  <input
                    type="checkbox"
                    checked={filterNoSale}
                    onChange={(e) => setFilterNoSale(e.target.checked)}
                    className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-xs text-slate-700 leading-tight">Χωρίς πώληση σε εξέλιξη</span>
                </label>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="w-full"
                >
                  Καθαρισμός
                </Button>
              </div>
            </div>
          </div>

          {/* Customer List */}
          <div className="space-y-3">
            {loading && customers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">Φόρτωση...</div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                {customers.length === 0 ? 'Δεν υπάρχουν πελάτες. Προσθέστε τον πρώτο!' : 'Δεν βρέθηκαν αποτελέσματα'}
              </div>
            ) : (
              filteredCustomers.map(customer => (
                <div
                  key={customer.customer_id}
                  className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/crm/customers/${customer.customer_id}`)}
                  data-testid={`customer-card-${customer.customer_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-5 w-5 text-slate-400" />
                        <h3 className="font-semibold text-slate-900">{customer.company_name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          customer.customer_type === 'existing' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {customer.customer_type === 'existing' ? 'Υφιστάμενη' : 'Υπό Σύσταση'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-slate-600">
                        {customer.tax_id && (
                          <div className="flex items-center gap-1 font-medium text-slate-700">
                            <FileText className="h-4 w-4" />
                            ΑΦΜ: {customer.tax_id}
                          </div>
                        )}
                        {customer.contact_person && (
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {customer.contact_person}
                          </div>
                        )}
                        {(customer.phone || customer.mobile) && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            {customer.mobile || customer.phone}
                          </div>
                        )}
                        {customer.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {customer.email}
                          </div>
                        )}
                        {customer.region_name && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {customer.region_name}
                          </div>
                        )}
                        {customer.eme && (
                          <div className="flex items-center gap-1 text-amber-700">
                            ΕΜΕ: {customer.eme}
                          </div>
                        )}
                      </div>
                      
                      {customer.program_names?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {customer.program_names.map((name, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {/* History Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-slate-700 border-slate-300 hover:bg-slate-100"
                        onClick={(e) => openHistoryDialog(customer, e)}
                        data-testid={`history-customer-${customer.customer_id}`}
                        title="Ιστορικό Επικοινωνιών"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      {/* New Communication Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        onClick={(e) => openCommunicationDialog(customer, e)}
                        data-testid={`quick-comm-${customer.customer_id}`}
                        title="Νέα Επικοινωνία"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEdit(customer); }}
                        data-testid={`edit-customer-${customer.customer_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDelete(customer.customer_id); }}
                        data-testid={`delete-customer-${customer.customer_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Επεξεργασία Πελάτη' : 'Νέος Πελάτης'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Type */}
            <div>
              <Label>Τύπος Πελάτη *</Label>
              <Select
                value={formData.customer_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, customer_type: v }))}
              >
                <SelectTrigger data-testid="customer-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Υφιστάμενη Επιχείρηση</SelectItem>
                  <SelectItem value="new">Υπό Σύσταση Επιχείρηση</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Επωνυμία *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label>Υπεύθυνος Επικοινωνίας</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                  data-testid="contact-person-input"
                />
              </div>
              <div>
                <Label>ΑΦΜ (9 ψηφία)</Label>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, tax_id: validateTaxId(e.target.value) }))}
                  placeholder="123456789"
                  maxLength={9}
                  data-testid="tax-id-input"
                />
                {formData.tax_id && formData.tax_id.length !== 9 && (
                  <p className="text-xs text-orange-600 mt-1">Πρέπει να είναι 9 ψηφία ({formData.tax_id.length}/9)</p>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label>Τηλέφωνο</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label>Κινητό</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                  data-testid="mobile-input"
                />
              </div>
              <div className="relative" ref={regionInputRef}>
                <Label>Περιφέρεια</Label>
                <div className="relative">
                  <Input
                    value={regionSearch}
                    onChange={(e) => {
                      setRegionSearch(e.target.value);
                      setShowRegionSuggestions(e.target.value.length > 0);
                      if (!e.target.value) {
                        setFormData(prev => ({ ...prev, region_id: '' }));
                        setShowRegionSuggestions(false);
                      }
                    }}
                    onFocus={() => regionSearch && setShowRegionSuggestions(true)}
                    placeholder="Πληκτρολογήστε για αναζήτηση..."
                    data-testid="region-search-input"
                  />
                  {regionSearch && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => {
                        setRegionSearch('');
                        setFormData(prev => ({ ...prev, region_id: '' }));
                        setShowRegionSuggestions(false);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {showRegionSuggestions && filteredRegions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredRegions.map(region => (
                      <button
                        key={region.region_id}
                        type="button"
                        className={`w-full px-3 py-2 text-left hover:bg-slate-100 ${
                          formData.region_id === region.region_id ? 'bg-slate-100 font-medium' : ''
                        }`}
                        onClick={() => selectRegion(region)}
                      >
                        {region.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Διεύθυνση</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                data-testid="address-input"
              />
            </div>

            {/* Existing Business Fields */}
            {formData.customer_type === 'existing' && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-slate-700 mb-3">Στοιχεία Υφιστάμενης Επιχείρησης</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Κατηγορία Επιχείρησης</Label>
                    <Select
                      value={formData.business_category}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, business_category: v }))}
                    >
                      <SelectTrigger data-testid="business-category-select">
                        <SelectValue placeholder="Επιλέξτε κατηγορία" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.category_id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ημ. Έναρξης</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label>ΕΜΕ (Ετήσιες Μονάδες Εργασίας)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.eme}
                      onChange={(e) => setFormData(prev => ({ ...prev, eme: e.target.value }))}
                      data-testid="eme-input"
                    />
                  </div>
                  <div>
                    <Label>Ετήσιος Τζίρος (€)</Label>
                    <Input
                      type="number"
                      value={formData.annual_revenue}
                      onChange={(e) => setFormData(prev => ({ ...prev, annual_revenue: e.target.value }))}
                      data-testid="annual-revenue-input"
                    />
                  </div>
                  <div>
                    <Label>ΚΑΔ Δραστηριότητας</Label>
                    <Input
                      value={formData.kad}
                      onChange={(e) => setFormData(prev => ({ ...prev, kad: e.target.value }))}
                      placeholder="π.χ. 47.11"
                      data-testid="kad-input"
                    />
                  </div>
                  <div>
                    <Label>Αρχείο ΚΑΔ</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        onChange={(e) => setKadFile(e.target.files?.[0] || null)}
                        className="text-sm"
                        data-testid="kad-file-input"
                      />
                      {kadFile && (
                        <span className="text-xs text-green-600">
                          {kadFile.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Υπό Σύσταση Business Fields */}
            {formData.customer_type === 'new' && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-slate-700 mb-3">Στοιχεία Υπό Σύσταση Επιχείρησης</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Κατηγορία Επιχείρησης</Label>
                    <Select
                      value={formData.business_category}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, business_category: v }))}
                    >
                      <SelectTrigger data-testid="new-business-category-select">
                        <SelectValue placeholder="Επιλέξτε κατηγορία" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.category_id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>ΚΑΔ Δραστηριότητας</Label>
                    <Input
                      value={formData.kad}
                      onChange={(e) => setFormData(prev => ({ ...prev, kad: e.target.value }))}
                      placeholder="π.χ. 62.01"
                      data-testid="new-kad-input"
                    />
                  </div>
                  <div>
                    <Label>Προγραμματισμένη Έναρξη</Label>
                    <Input
                      type="date"
                      value={formData.planned_start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, planned_start_date: e.target.value }))}
                      data-testid="planned-start-date-input"
                    />
                  </div>
                  <div>
                    <Label>Εκτιμώμενος Προϋπολογισμός (€)</Label>
                    <Input
                      type="number"
                      value={formData.estimated_budget}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimated_budget: e.target.value }))}
                      data-testid="estimated-budget-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Επιχειρηματική Ιδέα</Label>
                    <Textarea
                      value={formData.business_idea}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_idea: e.target.value }))}
                      rows={3}
                      data-testid="business-idea-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Program Interests */}
            <div className="border-t pt-4 mt-4">
              <Label className="mb-2 block">Προγράμματα Ενδιαφέροντος</Label>
              <div className="flex flex-wrap gap-2">
                {programs.map(program => (
                  <button
                    key={program.program_id}
                    type="button"
                    onClick={() => toggleProgramInterest(program.program_id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.program_interests?.includes(program.program_id)
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`program-toggle-${program.program_id}`}
                  >
                    {program.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Σημειώσεις</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                data-testid="notes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit" disabled={loading || uploadingKad} data-testid="save-customer-button">
                {loading || uploadingKad ? 'Αποθήκευση...' : (editingCustomer ? 'Ενημέρωση' : 'Δημιουργία')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Communication Dialog */}
      <Dialog open={commDialogOpen} onOpenChange={setCommDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Νέα Επικοινωνία
              {commCustomer && (
                <span className="block text-sm font-normal text-slate-600 mt-1">
                  {commCustomer.company_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateCommunication} className="space-y-4">
            <div>
              <Label>Τύπος Επικοινωνίας</Label>
              <Select
                value={commForm.communication_type}
                onValueChange={(v) => setCommForm(prev => ({ ...prev, communication_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Τηλεφωνική Κλήση</SelectItem>
                  <SelectItem value="meeting">Συνάντηση</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Program + Interest Status */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Πρόγραμμα</Label>
                <Select
                  value={commForm.program_id || 'none'}
                  onValueChange={(v) => setCommForm(prev => ({ ...prev, program_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="comm-program-select">
                    <SelectValue placeholder="Επιλέξτε..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Χωρίς πρόγραμμα —</SelectItem>
                    {programs.map(p => (
                      <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ενδιαφέρον</Label>
                <Select
                  value={commForm.interest_status || 'none'}
                  onValueChange={(v) => setCommForm(prev => ({ ...prev, interest_status: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="comm-interest-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="interested">Ενδιαφέρεται</SelectItem>
                    <SelectItem value="not_interested">Δεν ενδιαφέρεται</SelectItem>
                    <SelectItem value="undecided">Αναποφάσιστος</SelectItem>
                    <SelectItem value="no_answer">Δεν απάντησε</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Αποτέλεσμα *</Label>
              <Input
                value={commForm.outcome}
                onChange={(e) => setCommForm(prev => ({ ...prev, outcome: e.target.value }))}
                placeholder="π.χ. Ενδιαφέρεται για το πρόγραμμα"
                required
              />
            </div>
            
            <div>
              <Label>Σημειώσεις</Label>
              <Textarea
                value={commForm.notes}
                onChange={(e) => setCommForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Εκτιμώμενος Προϋπολογισμός (€)</Label>
                <Input
                  type="number"
                  value={commForm.estimated_budget}
                  onChange={(e) => setCommForm(prev => ({ ...prev, estimated_budget: e.target.value }))}
                />
              </div>
              <div>
                <Label>Ημερομηνία Ραντεβού *</Label>
                <Input
                  type="date"
                  value={commForm.next_contact_date?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = commForm.next_contact_time || '10:00';
                    setCommForm(prev => ({ ...prev, next_contact_date: date ? `${date}T${time}` : '', next_contact_time: time }));
                  }}
                />
              </div>
              <div>
                <Label>Ώρα</Label>
                <Select
                  value={commForm.next_contact_time || '10:00'}
                  onValueChange={(v) => {
                    const date = commForm.next_contact_date?.split('T')[0] || '';
                    setCommForm(prev => ({ ...prev, next_contact_time: v, next_contact_date: date ? `${date}T${v}` : '' }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ώρα" />
                  </SelectTrigger>
                  <SelectContent>
                    {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30','20:00'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Επιχειρηματική Ιδέα</Label>
              <Textarea
                value={commForm.business_idea}
                onChange={(e) => setCommForm(prev => ({ ...prev, business_idea: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Owner can assign to team member */}
            {user.role === 'owner' && teamMembers.length > 0 && (
              <div>
                <Label>Ανάθεση σε (προαιρετικό)</Label>
                <Select
                  value={commForm.assigned_to}
                  onValueChange={(v) => setCommForm(prev => ({ ...prev, assigned_to: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Εμένα (προεπιλογή)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Εμένα</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        {member.role_name || member.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {commForm.next_contact_date && (
              <p className="text-xs text-slate-500">
                Θα δημιουργηθεί αυτόματα ραντεβού
              </p>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCommDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit">
                Καταχώρηση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>
              Ιστορικό Επικοινωνιών - {historyCustomer?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {historyLoading ? (
              <div className="text-center py-8 text-slate-500">Φόρτωση...</div>
            ) : historyCommunications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Δεν υπάρχουν καταχωρημένες επικοινωνίες
              </div>
            ) : (
              historyCommunications.map(comm => (
                <div
                  key={comm.communication_id}
                  className="border rounded-lg p-3 hover:bg-slate-50"
                  data-testid={`history-comm-${comm.communication_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg shrink-0 ${
                        comm.communication_type === 'call' ? 'bg-blue-100 text-blue-700' :
                        comm.communication_type === 'meeting' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {comm.communication_type === 'call' ? <Phone className="h-4 w-4" /> :
                         comm.communication_type === 'meeting' ? <Calendar className="h-4 w-4" /> :
                         <Mail className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm">
                          {comm.communication_type === 'call' ? 'Τηλεφωνική Κλήση' :
                           comm.communication_type === 'meeting' ? 'Συνάντηση' : 'Email'}
                          {comm.interest_status === 'not_interested' && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                              Δεν Ενδιαφέρεται
                            </span>
                          )}
                          {comm.interest_status === 'interested' && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                              Ενδιαφέρεται
                            </span>
                          )}
                        </div>
                        {comm.outcome && (
                          <div className="text-sm text-slate-600 mt-1">{comm.outcome}</div>
                        )}
                        {comm.notes && (
                          <div className="text-sm text-slate-500 mt-2 p-2 bg-slate-50 rounded whitespace-pre-wrap">
                            {comm.notes}
                          </div>
                        )}
                        {(comm.program_name || comm.estimated_budget || comm.business_idea) && (
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            {comm.program_name && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-700 font-medium">
                                {comm.program_name}
                              </span>
                            )}
                            {comm.estimated_budget && (
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <Euro className="h-3 w-3" />
                                {Number(comm.estimated_budget).toLocaleString('el-GR')} €
                              </span>
                            )}
                            {comm.business_idea && (
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                <Lightbulb className="h-3 w-3" />
                                {comm.business_idea.length > 60 ? comm.business_idea.substring(0, 60) + '...' : comm.business_idea}
                              </span>
                            )}
                          </div>
                        )}
                        {comm.next_contact_date && (
                          <div className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Επόμενη επικοινωνία: {formatHistoryDate(comm.next_contact_date)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-500">{formatHistoryDate(comm.created_at)}</div>
                      {comm.employee_name && (
                        <div className="text-xs text-slate-400 mt-0.5">{comm.employee_name}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setHistoryDialogOpen(false);
                navigate(`/crm/customers/${historyCustomer?.customer_id}`);
              }}
              data-testid="history-open-detail-btn"
            >
              Άνοιγμα Καρτέλας
            </Button>
            <Button onClick={() => setHistoryDialogOpen(false)} data-testid="history-close-btn">
              Κλείσιμο
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMDashboard;
